/**
 * Schedule math — pure next-occurrence computation for payroll schedules.
 *
 * All schedule times are ET (America/New_York) wall-clock on 5-minute steps.
 * Pure CommonJS with no Node builtins (same rule as the engine) so the
 * frontend can import it directly for the "next fires" preview — see
 * src/pages/Payroll/engineClient.js for the bundling precedent (A-1/D-37).
 *
 * DST is handled by offset probing against Intl (no date libraries):
 *   - spring-forward gap (e.g. 02:30 on 2026-03-08 doesn't exist) rolls
 *     FORWARD one hour (fires 03:30 EDT);
 *   - fall-back ambiguity (01:30 twice on 2026-11-01) resolves to the FIRST
 *     (EDT) instant, so the schedule fires exactly once.
 */

const ET_TZ = 'America/New_York';
const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** ET wall-clock parts of an instant. */
function etParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const hour = parts.hour === '24' ? '00' : parts.hour; // some ICU builds emit 24:xx
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(hour),
    minute: Number(parts.minute),
    dow: DOW_NAMES.indexOf(parts.weekday),
  };
}

/**
 * UTC instant whose ET rendering equals the given ET wall-clock.
 * Offset probing: start from the wall-clock treated as UTC, correct by the
 * rendering difference until stable (≤3 iterations). Non-convergence means
 * the spring-forward gap → roll forward one hour.
 */
function etWallClockToUtc(year, month, day, hour, minute) {
  const target = Date.UTC(year, month - 1, day, hour, minute);
  let guess = target;
  for (let i = 0; i < 3; i++) {
    const p = etParts(new Date(guess));
    const rendered = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    if (rendered === target) return new Date(guess);
    guess += target - rendered;
  }
  // Spring-forward gap: guess oscillates ±1h around the nonexistent time.
  const p = etParts(new Date(guess));
  const rendered = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  if (rendered < target) guess += 3600000; // normalize to wall-clock + 1h
  return new Date(guess);
}

/** Days in month (month 1-12). */
function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 'HH:mm' on a 5-minute step, 00:00–23:55. */
function isValidTimeOfDay(s) {
  return /^([01][0-9]|2[0-3]):[0-5][05]$/.test(String(s || ''));
}

/**
 * Next fire instant STRICTLY AFTER `after` (Date), or null.
 * @param {object} schedule — { cadence: 'weekly'|'monthly', day_of_week (0-6),
 *   day_of_month (1-28 or -1 = last), time_of_day 'HH:mm' ET }
 */
function nextOccurrence(schedule, after = new Date()) {
  if (!schedule || !isValidTimeOfDay(schedule.time_of_day)) return null;
  const [hh, mm] = schedule.time_of_day.split(':').map(Number);
  const start = etParts(after);

  // Walk ET calendar days (UTC-noon anchor stays inside the same ET day).
  for (let i = 0; i <= 62; i++) {
    const cursor = new Date(Date.UTC(start.year, start.month - 1, start.day + i, 12));
    const d = etParts(cursor);
    let matches;
    if (schedule.cadence === 'weekly') {
      matches = d.dow === Number(schedule.day_of_week);
    } else if (schedule.cadence === 'monthly') {
      const wanted = Number(schedule.day_of_month) === -1
        ? lastDayOfMonth(d.year, d.month)
        : Number(schedule.day_of_month);
      matches = d.day === wanted;
    } else {
      return null;
    }
    if (!matches) continue;
    const instant = etWallClockToUtc(d.year, d.month, d.day, hh, mm);
    if (instant.getTime() > after.getTime()) return instant;
  }
  return null;
}

/** First N occurrences after `after` (UI preview). */
function nextOccurrences(schedule, count = 5, after = new Date()) {
  const out = [];
  let cursor = after;
  for (let i = 0; i < count; i++) {
    const next = nextOccurrence(schedule, cursor);
    if (!next) break;
    out.push(next);
    cursor = next;
  }
  return out;
}

/** Plain-English cadence ("Every Friday at 6:00 PM ET"). */
function describeCadence(schedule) {
  const [hh, mm] = String(schedule.time_of_day || '00:00').split(':').map(Number);
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const time = `${h12}:${String(mm).padStart(2, '0')} ${ampm} ET`;
  if (schedule.cadence === 'weekly') {
    return `Every ${DOW_FULL[Number(schedule.day_of_week)] || '?'} at ${time}`;
  }
  if (schedule.cadence === 'monthly') {
    const day = Number(schedule.day_of_month) === -1
      ? 'the last day of every month'
      : `day ${schedule.day_of_month} of every month`;
    return `On ${day} at ${time}`;
  }
  return '?';
}

module.exports = {
  ET_TZ,
  etParts,
  etWallClockToUtc,
  lastDayOfMonth,
  isValidTimeOfDay,
  nextOccurrence,
  nextOccurrences,
  describeCadence,
};
